<script setup lang="ts">
import { onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'
import { useBannerStore, type BannerView, type BannerVariant } from '@/stores/useBannerStore'

const bannerStore = useBannerStore()
const { visible } = storeToRefs(bannerStore)
const router = useRouter()

onMounted(() => {
  bannerStore.fetchVisible()
})

const VARIANT_CLASS: Record<BannerVariant, string> = {
  info: 'bg-info text-info-content',
  success: 'bg-success text-success-content',
  warning: 'bg-warning text-warning-content',
  error: 'bg-error text-error-content',
  neutral: 'bg-neutral text-neutral-content',
  custom: '',
}

function customStyle(b: BannerView) {
  if (b.variant !== 'custom') return undefined
  return { backgroundColor: b.bgColor ?? undefined, color: b.textColor ?? undefined }
}

function isExternal(link: string) {
  return /^https?:\/\//i.test(link)
}

function onClick(b: BannerView) {
  if (!b.link) return
  if (isExternal(b.link)) window.open(b.link, '_blank', 'noopener')
  else router.push(b.link)
}
</script>

<template>
  <div
    v-for="b in visible"
    :key="b.id"
    class="block shrink-0 border-b border-base-300/50 transition-opacity"
    :class="[VARIANT_CLASS[b.variant], b.link ? 'cursor-pointer hover:opacity-90' : '']"
    :style="customStyle(b)"
    :role="b.link ? 'button' : undefined"
    :tabindex="b.link ? 0 : undefined"
    @click="onClick(b)"
    @keydown.enter="onClick(b)"
  >
    <div class="overflow-hidden py-2">
      <div class="dyn-marquee-track flex w-max">
        <p class="dyn-marquee-segment shrink-0 px-6 text-center text-sm sm:text-base">{{ b.text }}</p>
        <p class="dyn-marquee-segment shrink-0 px-6 text-center text-sm sm:text-base" aria-hidden="true">
          {{ b.text }}
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.dyn-marquee-track {
  animation: dyn-marquee-scroll 42s linear infinite;
}
.dyn-marquee-track:hover {
  animation-play-state: paused;
}
@keyframes dyn-marquee-scroll {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
}
</style>
